import { Box, Chip, Typography } from '@mui/material'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useAtomValue, useSetAtom } from 'jotai'
import { useState, type MouseEvent } from 'react'
import {
  debouncedSearchQueryAtom,
  gatheringSidebarModeAtom,
  itemSearchQueryAtom,
  mainTabAtom,
  selectedGatheringAreaAtom,
  selectedGatheringAtom,
  selectedGatheringItemAtom,
} from '@/atoms'
import { useItemSearch } from '@/hooks/useItemSearch'
import { RecipeCard } from '@/components/ui/RecipeCard'
import { GATHERING_TYPE_LABEL } from '@/types/constants'
import type { GatherRecord, Recipe } from '@/types'

const PROB_COLOR: Record<string, string> = {
  A: '#ef5350',
  B: '#ff9800',
  C: '#fbc02d',
  D: '#66bb6a',
  E: '#90a4ae',
}

function CollapsibleSection({
  label,
  count,
  children,
}: {
  label: string
  count: number
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(true)
  return (
    <Box
      sx={{
        flex: open ? 1 : 'none',
        minHeight: 36,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          flexShrink: 0,
          cursor: 'pointer',
          userSelect: 'none',
          bgcolor: 'background.paper',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {count} 件
          </Typography>
        </Box>
        {open ? (
          <ExpandLessIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        ) : (
          <ExpandMoreIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        )}
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', display: open ? undefined : 'none' }}>{children}</Box>
    </Box>
  )
}

function GatherRecordRow({ record }: { record: GatherRecord }) {
  const setSearch = useSetAtom(itemSearchQueryAtom)
  const setMainTab = useSetAtom(mainTabAtom)
  const setGathering = useSetAtom(selectedGatheringAtom)
  const setGatheringArea = useSetAtom(selectedGatheringAreaAtom)
  const setGatheringItem = useSetAtom(selectedGatheringItemAtom)
  const setGatheringSidebarMode = useSetAtom(gatheringSidebarModeAtom)
  const isFishing = record.gatheringType === 'fishing'

  function handleGatheringSpotClick(event: MouseEvent) {
    event.stopPropagation()
    setMainTab('gathering')
    setGathering(record.gatheringType)
    setGatheringArea(record.area)
    setGatheringItem(null)
    setGatheringSidebarMode(isFishing ? 'item' : 'area')
  }

  const gatheringTypeLabel = GATHERING_TYPE_LABEL[record.gatheringType]

  return (
    <Box
      onClick={() => setSearch(record.name)}
      sx={{
        display: 'grid',
        gridTemplateColumns: isFishing ? '76px 56px minmax(0, 1fr)' : '76px minmax(0, 1fr) 28px',
        gap: 0.75,
        alignItems: 'center',
        px: 1.5,
        py: 0.4,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {isFishing ? (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            onClick={handleGatheringSpotClick}
            sx={{
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline', color: 'primary.main' },
            }}
          >
            {gatheringTypeLabel}
          </Typography>
          <Typography
            variant="caption"
            color="text.disabled"
            onClick={handleGatheringSpotClick}
            sx={{
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline', color: 'primary.main' },
            }}
          >
            {gatheringTypeLabel}
            {record.skillLevel}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            onClick={handleGatheringSpotClick}
            sx={{
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline', color: 'primary.main' },
            }}
            title={record.area}
          >
            {record.area}
          </Typography>
        </>
      ) : (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            onClick={handleGatheringSpotClick}
            sx={{
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline', color: 'primary.main' },
            }}
          >
            {gatheringTypeLabel}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              onClick={handleGatheringSpotClick}
              sx={{
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline', color: 'primary.main' },
              }}
            >
              {record.area}
            </Typography>
          </Box>
          {record.probability !== undefined && (
            <Chip
              label={record.probability}
              size="small"
              sx={{
                bgcolor: PROB_COLOR[record.probability] ?? '#e0e0e0',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.65rem',
                height: 16,
                width: 20,
                '& .MuiChip-label': { px: 0.25 },
              }}
            />
          )}
        </>
      )}
    </Box>
  )
}

export function ItemSearchPanel() {
  const { result } = useItemSearch()
  const debouncedQuery = useAtomValue(debouncedSearchQueryAtom)

  return (
    <Box
      sx={{
        gridArea: 'search',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderLeft: '1px solid',
        borderColor: 'divider',
      }}
    >
      {!debouncedQuery ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.disabled">
            アイテム名を入力してください
          </Typography>
        </Box>
      ) : (
        <>
          <CollapsibleSection label="作れるもの" count={result.usedIn.length}>
            {result.usedIn.length === 0 ? (
              <Typography variant="caption" color="text.disabled" sx={{ p: 1.5, display: 'block' }}>
                なし
              </Typography>
            ) : (
              result.usedIn.map((r: Recipe) => (
                <RecipeCard
                  key={r.id}
                  recipe={r}
                  highlightQuery={debouncedQuery}
                  hideIngredients
                  enableSkillNavigation
                />
              ))
            )}
          </CollapsibleSection>

          <CollapsibleSection label="作り方" count={result.canMake.length}>
            {result.canMake.length === 0 ? (
              <Typography variant="caption" color="text.disabled" sx={{ p: 1.5, display: 'block' }}>
                なし
              </Typography>
            ) : (
              result.canMake.map((r: Recipe) => (
                <RecipeCard
                  key={r.id}
                  recipe={r}
                  highlightQuery={debouncedQuery}
                  hideIngredients
                  enableSkillNavigation
                />
              ))
            )}
          </CollapsibleSection>

          <CollapsibleSection label="採れる場所" count={result.gatheredAt.length}>
            {result.gatheredAt.length === 0 ? (
              <Typography variant="caption" color="text.disabled" sx={{ p: 1.5, display: 'block' }}>
                なし
              </Typography>
            ) : (
              result.gatheredAt.map((rec: GatherRecord) => (
                <GatherRecordRow key={rec.id} record={rec} />
              ))
            )}
          </CollapsibleSection>
        </>
      )}
    </Box>
  )
}
